import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, gbp, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import AuthPanel from "@/components/AuthPanel";
import Footer from "@/components/Footer";
import { T } from "@/constants/testIds";

export default function Checkout() {
  const { items, subtotal, shipping, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [useCredit, setUseCredit] = useState(true);
  const [prefilled, setPrefilled] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address_line: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  });

  useEffect(() => {
    if (user && user.id && !prefilled) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        address_line: user.default_address?.address_line || f.address_line,
        city: user.default_address?.city || f.city,
        postcode: user.default_address?.postcode || f.postcode,
        country: user.default_address?.country || f.country,
      }));
      setPrefilled(true);
    }
  }, [user, prefilled]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.name.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.address_line.trim() &&
    form.city.trim() &&
    form.postcode.trim();

  if (items.length === 0)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FAF8F4] px-4 text-center">
        <p className="font-display text-4xl font-black uppercase text-stone-950">Your bag is empty</p>
        <p className="text-sm text-stone-500">Add a piece before checking out.</p>
        <button
          onClick={() => navigate("/shop")}
          className="mt-2 bg-[#E52E39] px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white"
        >
          Browse the shop
        </button>
      </div>
    );

  if (user === null)
    return <div className="min-h-screen bg-[#FAF8F4]" />;

  if (!user)
    return (
      <div className="min-h-screen">
        <AuthPanel title="Sign in to check out" subtitle="Browsing is free — you only need an account to buy." />
        <Footer />
      </div>
    );

  const creditAvailable = Math.min(user.credit_balance || 0, subtotal + shipping);
  const creditApplied = useCredit ? creditAvailable : 0;
  const remaining = Math.max(0, Math.round((subtotal + shipping - creditApplied) * 100) / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fill in every delivery field (with a valid email).");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map(({ product_id, size, colour, qty }) => ({ product_id, size, colour, qty })),
        customer: form,
        use_credit: creditApplied > 0,
      });
      if (order.status === "paid") {
        clear();
        toast.success("Paid with your store credit — thank you!");
        navigate("/payment/success?credit=1");
        return;
      }
      const { data: pay } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      if (pay.checkout_url) {
        window.location.href = pay.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-8 lg:px-16">
        <h1 className="font-display text-4xl font-black uppercase tracking-tight text-stone-950 sm:text-5xl">Checkout</h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-stone-500">
          Signed in as {user.email} • card payment via Stripe
        </p>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_420px]">
          <form onSubmit={submit} className="space-y-6">
            <div>
              <p className="via-label">Full name</p>
              <input data-testid="checkout-name-input" className="via-input" value={form.name} onChange={set("name")} placeholder="Juan Karran" />
            </div>
            <div>
              <p className="via-label">Email</p>
              <input data-testid="checkout-email-input" type="email" className="via-input" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
            <div>
              <p className="via-label">Delivery address</p>
              <input data-testid="checkout-address-input" className="via-input" value={form.address_line} onChange={set("address_line")} placeholder="Street, house / flat" />
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                These details are saved to your account automatically
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="via-label">Town / city</p>
                <input data-testid="checkout-city-input" className="via-input" value={form.city} onChange={set("city")} placeholder="Douglas" />
              </div>
              <div>
                <p className="via-label">Postcode</p>
                <input data-testid="checkout-postcode-input" className="via-input" value={form.postcode} onChange={set("postcode")} placeholder="IM1 2AB" />
              </div>
            </div>
            <div>
              <p className="via-label">Country</p>
              <select data-testid="checkout-country-select" className="via-input" value={form.country} onChange={set("country")}>
                {["United Kingdom", "Isle of Man", "Ireland", "France", "Germany", "Spain", "Netherlands", "Other (EU)"].map((c) => (
                  <option key={c} value={c} className="bg-white">
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {(user.credit_balance || 0) > 0 && (
              <label data-testid={T.checkoutUseCredit} className="flex cursor-pointer items-start gap-3 border border-[#C79500]/50 bg-[#FFFBEF] p-4">
                <input
                  type="checkbox"
                  checked={useCredit}
                  onChange={(e) => setUseCredit(e.target.checked)}
                  className="mt-0.5 accent-[#E52E39]"
                />
                <span className="text-sm text-stone-700">
                  Use my store credit — <span className="font-bold text-[#C79500]">{gbp(creditAvailable)} applied</span>{" "}
                  (you have {gbp(user.credit_balance)})
                </span>
              </label>
            )}

            <button
              data-testid={remaining === 0 ? T.checkoutPayCredit : T.checkoutPay}
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-3 bg-[#E52E39] py-4 font-display text-xl font-black uppercase tracking-wide text-white transition-all hover:bg-[#c9202a] hover:shadow-[0_0_30px_rgba(229,46,57,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Lock className="h-4 w-4" />
              {busy ? "Starting secure checkout…" : remaining === 0 ? "Place order — pay with credit" : `Pay ${gbp(remaining)} by card`}
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-widest text-stone-500">
              {remaining === 0 ? "No card needed — covered by your credit" : "You'll confirm payment on the secure Stripe page"}
            </p>
          </form>

          <aside className="h-fit border border-stone-200 bg-white p-6 lg:sticky lg:top-28">
            <h2 className="font-display text-2xl font-black uppercase tracking-wide text-stone-950">Your order</h2>
            <ul className="my-5 space-y-4">
              {items.map((it) => (
                <li key={`${it.product_id}-${it.size}`} className="flex gap-3">
                  <div className="h-16 w-14 shrink-0 overflow-hidden bg-[#F1EEE6]">
                    {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex flex-1 flex-col justify-center">
                    <p className="font-display text-sm font-bold uppercase leading-tight text-stone-950">{it.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
                      Size {it.size} × {it.qty}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-stone-400">{it.delivery_time}</p>
                  </div>
                  <span className="self-center font-mono text-sm font-semibold text-[#C79500]">
                    {gbp(it.price * it.qty)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="space-y-2 border-t border-stone-200 pt-4 font-mono text-xs uppercase tracking-widest text-stone-500">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{gbp(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery</span>
                <span>{shipping === 0 ? "FREE" : gbp(shipping)}</span>
              </div>
              {creditApplied > 0 && (
                <div className="flex justify-between text-[#C79500]">
                  <span>Store credit</span>
                  <span>−{gbp(creditApplied)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-stone-200 pt-3 text-base font-bold text-stone-950">
                <span>{remaining === 0 ? "Paid in full" : "Card payment"}</span>
                <span className="text-[#C79500]">{gbp(remaining)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
